import angular from 'angular';
import { prop } from 'micro-fp';

import _toggleFilter from '../../shifts/components/filters/toggleFilter';
import { EVENT_TYPES } from '../../shifts/constants';
import { ROSTER_SORT_MAP } from '../constants';

const DEFAULT_GROUP_METHOD = 'username';
class RosterService {
  tableData = null;
  date = new Date();

  constructor(
    Authentication,
    EventCollection,
    DayPartsService,
    PersonaCollection,
    Utils,
    sdk,
    $filter
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      EventCollection,
      DayPartsService,
      PersonaCollection,
      Utils,
      $filter,
    });

    this.canSeeUserShifts = sdk('process/canSeeUserShifts');
  }

  get whoseEvents() {
    return this.filter.userIds.map((id) => this.PersonaCollection.personasDict[id]).filter(Boolean);
  }

  get tableSortChain() {
    const isReverse = this.sortMethod[0] === '-';
    const columnProperty = isReverse ? this.sortMethod.slice(1) : this.sortMethod;

    const sortChain =
      columnProperty in ROSTER_SORT_MAP ? ROSTER_SORT_MAP[columnProperty] : ROSTER_SORT_MAP.default;
    return isReverse ? `-${sortChain}` : sortChain;
  }

  get dayParts() {
    return this.DayPartsService.dayParts;
  }

  init() {
    this.initRosterFilters();
    this.initPersonas();
    this.initSortMethods();
  }

  initPersonas() {
    this.refreshPositionsAndLocations();
    this.tags = this.PersonaCollection.tags;
    this.userGroups = this.PersonaCollection.groups.filter((g) => !g.hiddenOnSchedule);
    this.users = this.PersonaCollection.allUsers
      .filter(this.canSeeUserShifts)
      .filter((u) => u && !u.hiddenOnSchedule);

    this.userOptions = this.Utils.sortBy(this.users, 'fullname').map((user) => ({
      id: user.id,
      name: user.fullname,
      fullname: user.fullname,
    }));
  }

  setTableData(data) {
    this.tableData = data;
    this.refreshPositionsAndLocations();
  }

  refreshPositionsAndLocations() {
    this.locations = this.PersonaCollection.scheduleLocations;
    this.positions = this.PersonaCollection.schedulePositions;

    if (!this.tableData?.length) {
      return;
    }

    const locationsDict = this.Utils.arrayToDict(this.locations.map(prop('id')));
    const positionsDict = this.Utils.arrayToDict(this.positions.map(prop('id')));

    this.tableData.forEach((event) => {
      if (event.isGroupHeader) {
        return;
      }

      if (!locationsDict[event.location.id]) {
        this.locations.push(event.location);
        locationsDict[event.location.id] = true;
      }

      if (!positionsDict[event.position.id]) {
        this.positions.push(event.position);
        positionsDict[event.position.id] = true;
      }
    });
  }

  initRosterFilters() {
    this.defaultFilter = this._getDefaultFilter();

    const { clientcache } = this.Authentication.user;
    if (!('roster' in clientcache)) {
      (clientcache.roster = {
        filter: angular.copy(this.defaultFilter),
        filtersOpened: false,
        groupMethod: DEFAULT_GROUP_METHOD,
      }),
        this.Authentication.syncClientCache();
    }

    if (clientcache.roster.filter.eventTypes.length !== this.defaultFilter.eventTypes) {
      clientcache.roster.filter.eventTypes = this.defaultFilter.eventTypes;
      this.Authentication.syncClientCache();
    }

    this.filtersOpened = this.Authentication.user.clientcache.roster.filtersOpened;
    this.filter = angular.copy(this.Authentication.user.clientcache.roster.filter);
    this.checkFilterIntegrity();
    this.filtersEmpty = this.isFilterEmpty();
  }

  checkFilterIntegrity() {
    Object.keys(this.defaultFilter).forEach((filterKey) => {
      if (!(filterKey in this.filter)) {
        this.filter[filterKey] = this.defaultFilter[filterKey];
      }
    });

    Object.keys(this.filter).forEach((filterKey) => {
      if (!(filterKey in this.defaultFilter)) {
        delete this.filter[filterKey];
      }
    });
  }

  syncCache() {
    this.Authentication.user.clientcache.roster = {
      filter: this.filter,
      filtersOpened: this.filtersOpened,
      filtersEmpty: this.filtersEmpty,
      groupMethod: this.groupMethod,
    };

    this.Authentication.syncClientCache();
  }

  initSortMethods() {
    const ctrl = this;
    this.groupMethod =
      this.Authentication.user.clientcache.roster?.groupMethod ?? DEFAULT_GROUP_METHOD;

    this.groupMethodOptions = [
      { value: 'username', label: 'Employees' },
      {
        value: 'position',
        label: 'Positions',
        get hidden() {
          return ctrl.positions.length < 2;
        },
      },
      {
        value: 'location',
        label: 'Locations',
        get hidden() {
          return ctrl.locations.length < 2;
        },
      },
      {
        value: 'group',
        label: 'Groups',
        get hidden() {
          return ctrl.userGroups.length < 2;
        },
      },
      {
        value: 'tag',
        label: 'Tags',
        get hidden() {
          return ctrl.tags.length < 2;
        },
      },
      {
        value: 'dayPart',
        label: 'Day parts',
        get hidden() {
          return ctrl.dayParts.length < 2;
        },
      },
    ];
  }

  changeGroupMethod(method) {
    this.groupMethod = method;
    this.Authentication.user.clientcache.roster.groupMethod = method;
    this.Authentication.syncClientCache();
  }

  setFiltersOpened(value) {
    this.filtersOpened = value;
    this.Authentication.user.clientcache.roster.filtersOpened = value;
    this.Authentication.syncClientCache();
  }

  onFiltersChange() {
    this.filtersEmpty = this.isFilterEmpty();
    this.syncCache();
    this.refreshPositionsAndLocations();
  }

  isFilterEmpty() {
    return JSON.stringify(this.defaultFilter) === JSON.stringify(this.filter);
  }

  clearAllFilters() {
    this.filter = angular.copy(this.defaultFilter);
    this.filtersEmpty = true;
    this.syncCache();
  }

  getRosterReport(from, to = from) {
    return this.Authentication.sendRequest({
      path: `reports/roster?dates=${from}/${to}&v=2`,
    }).then(({ data }) => ({
      events: data.events,
      hasShiftsWithinTwoWeeks: data.hasShiftsWithinTwoWeeks,
    }));
  }

  _getDefaultFilter() {
    return {
      eventTypes: [EVENT_TYPES.shift, EVENT_TYPES.noShow, EVENT_TYPES.sickCallout],
      locationIds: [],
      positionIds: [],
      tagIds: [],
      groupIds: [],
      userIds: [],
      dayPartIds: [],
    };
  }

  toggleFilter(...args) {
    _toggleFilter(this, ...args);
  }
}

export default RosterService;
